<template>
    <div class="note-info">
        <frm-panel v-if="loading" compact :title="$t('calendar.note')">
            <div class="info-list">
                <frm-spinner />
            </div>
        </frm-panel>
        <frm-panel v-else compact :title="$t('calendar.note')" class="note-info__panel">
            <template #header-extra>
                <span v-if="noteSaved" class="panel__header-note-saved" data-testid="calendar-note:note-saved">
                    <frm-icon name="check" class="panel__header-note-saved-icon" />
                    {{ $t('calendar.appointment.note-saved') }}
                </span>
            </template>
            <textarea-autosize ref="textarea"
                               v-model="note"
                               class="form-textarea form-textarea--required note-info__note"
                               :min-height="100"
                               data-testid="calendar-note:note-textarea"
            />
        </frm-panel>
    </div>
</template>

<script>
import jsonApi from '../../json-api-client';
import DialogManager from '../../services/DialogManager';
import debounce from '../../services/debounce';

export default {
    props: {
        event: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,
            note: '',
            noteSaved: false,
            lastSavedNote: '',
        };
    },

    computed: {
        noteHasChanged() {
            // Show action buttons if note has been changed
            return this.note !== this.lastSavedNote;
        },
    },

    watch: {
        noteHasChanged(noteChanged) {
            if (noteChanged) {
                this.noteSaved = false;
            }
        },

        loading(loading) {
            if (!loading) {
                this.$nextTick(() => this.$refs.textarea.$el.focus());
            }
        },

        note: debounce(function () {
            if (this.noteHasChanged) {
                this.saveNote();
            }
        }, 500),

        event: {
            handler(newValue, oldValue) {
                if (newValue.id === oldValue?.id) {
                    return;
                }

                this.fetchNote(newValue.id, !oldValue);
            },
            immediate: true,
        },
    },

    methods: {
        async fetchNote(noteId, showLoader = true) {
            this.loading = showLoader;

            try {
                const [note] = await Promise.all([
                    jsonApi.find('calendar_note', noteId).then(response => response.data),
                    // Make sure the loader is shown at least 300 ms to prevent a "Flash Of Loading Content"™
                    new Promise(resolve => setTimeout(() => resolve(), 300)),
                ]);

                this.note = note.description;
                this.lastSavedNote = note.description;
            } catch (errors) {
                await DialogManager.errors(errors);
            } finally {
                this.loading = false;
            }
        },

        async saveNote() {
            const note = this.note;
            try {
                await jsonApi.update('calendar_note', { id: this.event.id, description: note });
            } catch (errors) {
                return DialogManager.errors(errors);
            }

            this.noteSaved = true;
            this.lastSavedNote = note;

            return this.$store.dispatch('calendar/fetchEvents');
        },
    },
};
</script>

<style lang="scss">
.note-info__panel {
    .panel__content {
        display: flex;
        padding: 0;
    }
}

.note-info__note {
    border-radius: 0 0 $border-radius-lg $border-radius-lg;
    flex-grow: 1;
    margin: -1px;
}
</style>
